import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { SpinnerService } from '../../services/spinner.service';
import { ApiReservationDownloadFile, ApiCreateReservationDownloadFilesRequest } from '../../api-client-module';
import { ReservationDownloadFile } from '../../firestore-models/reservation-download-file';
import { ReservationFacility } from '../../firestore-models/reservation-facility';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface ReservationDownloadDialogData {
  change_type: number;
  reservationDownloadFile?: ApiReservationDownloadFile;
  reservationFacilities: Array<ReservationFacility>;
}

@Component({
  selector: 'app-reservation-download-dialog',
  templateUrl: './reservation-download-dialog.component.html',
  styleUrls: ['./reservation-download-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class ReservationDownloadDialogComponent implements OnInit {

  //予約ダウンロードファイル情報
  reservationDownloadFile?: ApiReservationDownloadFile;
  reservationFacilities!: Array<ReservationFacility>;
  //プロジェクトID
  projectId: string;
  projectLanguages!: Array<ProjectLanguage>;
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理
    
  isAdded: boolean = false;

  //フォーム
  reservationDownloadFormGroup!: FormGroup;
  reservationFacilityIdControl!: FormControl;
  memoControl!: FormControl;
  startDateControl!: FormControl;
  endDateControl!: FormControl;

  projectBaseLang: string = 'ja';

  private reservationDownloadFilesCollection!: AngularFirestoreCollection<ReservationDownloadFile>;
  reservationDownloadFiles!: Observable<ReservationDownloadFile[]>;

  constructor(
    public dialogRef: MatDialogRef<ReservationDownloadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReservationDownloadDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.reservationFacilities = this.data.reservationFacilities;
    this.reservationDownloadFilesCollection = this.angularFirestore.collection<ReservationDownloadFile>(
      AppConst.FIRESTORE_RESERVATION_DOWNLOAD_FILES, ref => ref.where('project_id', '==', this.projectId));
    this.reservationDownloadFiles = this.reservationDownloadFilesCollection.valueChanges({ idField: 'id' });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.reservationDownloadFile = this.data.reservationDownloadFile;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
    if (!this.reservationDownloadFile) {
      this.reservationDownloadFile = {
        reservation_facility_id: '',
        memo: '',
        start_date: null as any,
        end_date: null as any,
        project_id: this.projectId,
        enabled: true
      };
      this.isAdded = true;
    }

    this.createForm();
    this.reservationFacilityIdControl = this.reservationDownloadFormGroup.get('reservationFacilityId') as FormControl;
    this.memoControl = this.reservationDownloadFormGroup.get('memo') as FormControl;
    this.startDateControl = this.reservationDownloadFormGroup.get('startDate') as FormControl;
    this.endDateControl = this.reservationDownloadFormGroup.get('endDate') as FormControl;
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToEndDate() {
    return this.translate.instant('作成開始日より前の日は設定不可');
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.reservationDownloadFormGroup = this.formBuilder.group(
      {
        reservationFacilityId: [this.reservationDownloadFile!.reservation_facility_id, [Validators.required]],
        memo: [this.reservationDownloadFile!.memo],
        startDate: [this.reservationDownloadFile!.start_date, []],
        endDate: [this.reservationDownloadFile!.end_date, []],
      }, {
        validator: this.isValidDate
      }
    );
  }

  //更新ボタン
  create(): void {
    if (this.reservationDownloadFormGroup.valid) {
      this.reservationDownloadFile!.reservation_facility_id = this.reservationFacilityIdControl.value;
      this.reservationDownloadFile!.memo = this.memoControl.value;
      this.reservationDownloadFile!.start_date = this.startDateControl.value ? this.startDateControl.value : new Date('1970-01-01T00:00:00');
      this.reservationDownloadFile!.end_date = this.endDateControl.value ? this.endDateControl.value : new Date('2099-12-31T23:59:59');
      if (this.reservationDownloadFile) {
        let request: ApiCreateReservationDownloadFilesRequest = {
          access_token: '',
          change_type: this.data.change_type,
          reservation_download_files: [this.reservationDownloadFile]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.createReservationDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('作成を依頼しました。'));
              this.dialogRef.close();
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // 作成期間整合制チェック
  isValidDate(ac: AbstractControl) {
    const startDate = ac.get('startDate');
    const endDate = ac.get('endDate');
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
          endDate.setErrors({ before_start_date: true });
      } else {
        endDate.setErrors(null);
      }
    }
  }

}
